import React, { useCallback, useEffect, useReducer, useState } from 'react'
import { Button, Stepper } from '@enamelai/design-system'
import PersonalInfoStep from '@components/layout/Modals/AddPatientModal/PersonalInfoStep'
import BackgroundStep from '@components/layout/Modals/AddPatientModal/BackgroundStep'
import PatientReview from '@components/layout/Modals/AddPatientModal/PatientReview'
import { validatePatientStep } from '@components/layout/Modals/AddPatientModal/patientForm.logic'
import { initialState, reducer } from '@store/reducers/addPatient.reducer'
import type { Patient } from '@dataTypes/PatientInfo.types'
import type {
  Allergy,
  MedicalCondition,
} from '@dataTypes/allergy-medical-condition.types'
import { GetAllAllergies } from '@services/api/allergy.api'
import { GetAllMedicalConditions } from '@services/api/medical-condition.api'
import {
  createKioskPatient,
  freshKioskPatientData,
} from './kioskPatientRegistration.logic'
import './KioskPatientRegistration.css'

const STEPS = ['Personal', 'Background', 'Review']

interface KioskPatientRegistrationProps {
  clinicId: string
  accessToken: string
  actorId: string
  onCancel: () => void
  onCreated: (patientId: string) => void
  onDuplicate: () => void
}

const KioskPatientRegistration: React.FC<KioskPatientRegistrationProps> = ({
  clinicId,
  accessToken,
  actorId,
  onCancel,
  onCreated,
  onDuplicate,
}) => {
  const [step, setStep] = useState(1)
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    patientData: freshKioskPatientData(),
  })
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [allergiesCatalog, setAllergiesCatalog] = useState<Allergy[]>([])
  const [medicalConditionsCatalog, setMedicalConditionsCatalog] = useState<
    MedicalCondition[]
  >([])

  useEffect(() => {
    let cancelled = false
    const requestOptions = { accessToken, clinicId }

    void Promise.all([
      GetAllAllergies(requestOptions),
      GetAllMedicalConditions(requestOptions),
    ])
      .then(([allergies, medicalConditions]) => {
        if (cancelled) return
        setAllergiesCatalog(allergies.filter((item) => !item.is_hidden))
        setMedicalConditionsCatalog(
          medicalConditions.filter((item) => !item.is_hidden),
        )
      })
      .catch(() => {
        if (cancelled) return
        setAllergiesCatalog([])
        setMedicalConditionsCatalog([])
      })

    return () => {
      cancelled = true
    }
  }, [accessToken, clinicId])

  const handleInputChange = useCallback(
    (
      field: keyof Patient,
      value: string | number | boolean | string[] | File[] | undefined,
    ): void => {
      dispatch({ type: 'SET_FIELD', field, value })
      setSubmitError('')
    },
    [],
  )

  const handleNext = (): void => {
    if (step <= 2) {
      const errors = validatePatientStep(state.patientData, step === 1 ? 1 : 2)
      dispatch({ type: 'SET_ERRORS', errors })
      if (Object.keys(errors).length > 0) return
    }
    setStep((current) => Math.min(current + 1, STEPS.length))
  }

  const handleSubmit = async (): Promise<void> => {
    const personalErrors = validatePatientStep(state.patientData, 1)
    const backgroundErrors = validatePatientStep(state.patientData, 2)
    const errors = { ...personalErrors, ...backgroundErrors }
    if (Object.keys(errors).length > 0) {
      dispatch({ type: 'SET_ERRORS', errors })
      setStep(Object.keys(personalErrors).length > 0 ? 1 : 2)
      return
    }

    setSubmitting(true)
    setSubmitError('')
    try {
      const result = await createKioskPatient(
        state.patientData,
        clinicId,
        accessToken,
        actorId,
      )
      dispatch({ type: 'RESET', payload: freshKioskPatientData() })
      if (result.kind === 'duplicate') {
        onDuplicate()
        return
      }
      onCreated(result.patientId)
    } catch (error) {
      setSubmitError(
        'We could not create your patient record. Please check your information and try again, or ask the front desk for help.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="kiosk-registration" aria-label="New patient registration">
      <div className="kiosk-registration__intro">
        <p>Please enter your information.</p>
      </div>
      <div className="kiosk-registration__card">
        <Stepper
          className="kiosk-registration__stepper"
          steps={STEPS}
          currentStep={step - 1}
        />
        <div className="kiosk-registration__content add-patient-modal">
          {step === 1 ? (
            <PersonalInfoStep
              patientData={state.patientData}
              handleInputChange={handleInputChange}
              validationErrors={state.validationErrors}
            />
          ) : step === 2 ? (
            <BackgroundStep
              patientData={state.patientData}
              handleInputChange={handleInputChange}
              validationErrors={state.validationErrors}
              kioskMode
              clinicId={clinicId}
              accessToken={accessToken}
              allergiesCatalog={allergiesCatalog}
              medicalConditionsCatalog={medicalConditionsCatalog}
            />
          ) : (
            <PatientReview
              patientData={state.patientData}
              kioskMode
              allergiesCatalog={allergiesCatalog}
              medicalConditionsCatalog={medicalConditionsCatalog}
            />
          )}
        </div>
      </div>
      {submitError && (
        <div className="kiosk-form-verify-error" role="alert">
          {submitError}
        </div>
      )}
      <div className="kiosk-registration__actions">
        <Button
          variant="secondary"
          size="md"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel &amp; Start over
        </Button>
        {step > 1 && (
          <Button
            className="kiosk-registration__back"
            variant="secondary"
            size="md"
            onClick={() => setStep((current) => current - 1)}
            disabled={submitting}
          >
            Back
          </Button>
        )}
        <Button
          variant="primary"
          size="md"
          onClick={step === STEPS.length ? handleSubmit : handleNext}
          disabled={submitting}
        >
          {submitting
            ? 'Submitting registration...'
            : step === STEPS.length
              ? 'Submit registration'
              : 'Continue'}
        </Button>
      </div>
    </div>
  )
}

export default KioskPatientRegistration
